import {
  type WithdrawalAmountValidation,
  isBlacklistedWithdrawalAddress,
} from '@funkit/connect-core'
import { zeroAddress } from 'viem'

import type {
  WithdrawalFlowConfig,
  WithdrawalToken,
} from '../flow/withdrawalFlowConfig'
import { useDynamicConfig } from '../statsig/useDynamicConfig'
import { useSourceTokenDecimals } from './useSourceTokenDecimals'
import { useSourceTokenUsdPrice } from './useSourceTokenUsdPrice'
import {
  type WithdrawCtaBlocker,
  getWithdrawCtaBlocker,
  isNoOpSelfWithdrawal,
  validateAmount,
} from './validation'

export interface CryptoWithdrawalValidation {
  /** Recipient the funds would be lost in (e.g. a token contract). */
  isRecipientBlacklisted: boolean
  amountValidation: WithdrawalAmountValidation
  /** The single thing left to fix, or `undefined` when the form is complete. */
  ctaBlocker: WithdrawCtaBlocker | undefined
}

/**
 * Everything the crypto withdrawal form knows synchronously about what the user
 * has typed. The Statsig-backed blacklist is why this is a hook; the verdicts
 * themselves come from the pure functions in `validation.ts`.
 */
export function useCryptoWithdrawalValidation({
  config,
  sourceToken,
  destinationToken,
  amount,
  recipient,
  balance,
}: {
  config: WithdrawalFlowConfig
  sourceToken: WithdrawalToken
  destinationToken: WithdrawalToken | undefined
  amount: string
  recipient: string
  balance: string | undefined
}): CryptoWithdrawalValidation {
  const blacklist = useDynamicConfig('blacklistedwithdrawaladdress')
  const withdrawalConfig = useDynamicConfig('withdrawalconfig')
  const isRecipientBlacklisted = isBlacklistedWithdrawalAddress({
    address: recipient,
    blacklist,
    withdrawalConfig,
  })

  // Inert until the price resolves, so the USD limits switch on rather than
  // rejecting an amount against a price that isn't known yet.
  const { data: sourceTokenUsdPrice } = useSourceTokenUsdPrice(sourceToken)
  // Shares the form's own read (same query key), so this costs no extra call.
  // A USD floor converts to a token figure finer than the field can hold, and
  // the typed minimum would sit just under it without this.
  const sourceTokenDecimals = useSourceTokenDecimals(sourceToken)

  const amountValidation = validateAmount({
    config,
    sourceToken,
    sourceTokenUsdPrice,
    sourceTokenDecimals,
    amount,
    balance,
  })

  return {
    isRecipientBlacklisted,
    amountValidation,
    ctaBlocker: getWithdrawCtaBlocker({
      recipientAddress: recipient,
      isRecipientBlacklisted,
      isRecipientSameAsSource: isNoOpSelfWithdrawal({
        recipientAddress: recipient,
        // Walletless: no source address to collide with, and the placeholder
        // can never be a real recipient, so the self-send check stays inert.
        walletAddress: config.withdrawalWallet?.address() ?? zeroAddress,
        sourceToken,
        destinationToken,
        isVenueRail: Boolean(config.submitWithdrawal),
      }),
      amount,
      amountValidation,
      destinationToken,
    }),
  }
}
